import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  FieldTypeModalComponent,
  IFieldTypeModalResult,
} from '../../../../components/field-type-modal/field-type-modal.component';
import { EmptyStateComponent } from '../../../../components/empty-state/empty-state.component';
import { SkeletonComponent } from '../../../../components/skeleton/skeleton.component';
import { FieldTypeModel } from '../../../../models/field-type.model';
import { DateToMomentPipe } from '../../../../pipes/date-to-moment.pipe';
import { ConfirmService } from '../../../../services/confirm.service';
import { FieldTypeService } from '../../../../services/field-type.service';
import { injectIsMobile } from '../../../../utils/breakpoint.util';

/** Opciones del filtro de estado. `all` no manda el query param. */
type StatusFilter = 'all' | 'active' | 'inactive';

/**
 * Catalogo de tipos de campo: tabla en escritorio, tarjetas en móvil (mismo
 * patrón que `templates-page` y `users-page`, ver `breakpoint.util.ts`).
 *
 * Sin página de detalle: se edita en modal, así que ni la fila ni la tarjeta
 * son clicables (`RowLinkDirective` no aplica aquí — inventaría un destino
 * inexistente).
 *
 * El `fieldType` es la PK natural: se captura al crear y queda fijo. El borrado
 * es SOFT (deja `status = false`), asi que la fila sigue visible y se puede
 * reactivar.
 */
@Component({
  selector: 'app-field-types-page',
  imports: [DateToMomentPipe, EmptyStateComponent, FormsModule, NgSelectComponent, SkeletonComponent],
  templateUrl: './field-types-page.component.html',
  styleUrl: './field-types-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldTypesPageComponent implements OnInit {
  private readonly fieldTypeService = inject(FieldTypeService);
  private readonly modalService = inject(NgbModal);
  private readonly confirmService = inject(ConfirmService);

  protected readonly loading = this.fieldTypeService.loading;
  protected readonly fieldTypes = this.fieldTypeService.fieldTypes;

  /** Ver doc en `templates-page.component.ts`: misma función de inyección. */
  protected readonly isMobile = injectIsMobile();

  /** Mismo número de bloques que el esqueleto de filas en escritorio (6). */
  protected readonly skeletonCards = [0, 1, 2, 3, 4, 5];

  /**
   * `loaded` arranca en `false` y solo pasa a `true` cuando la peticion TERMINA
   * (con exito o con error). `loading` no basta: arranca en `false`, asi que el
   * primer render pintaria el estado vacio antes de saber si hay tipos.
   */
  protected readonly loaded = this.fieldTypeService.fieldTypesLoaded;
  protected readonly error = this.fieldTypeService.fieldTypesError;

  protected readonly statusFilter = signal<StatusFilter>('all');
  protected readonly search = signal('');

  /**
   * Opciones fijas del filtro de estado. "Todos los estados" es un item REAL
   * de la lista (no un placeholder): el ng-select se comporta identico al
   * `<select>` nativo que reemplaza.
   */
  protected readonly statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'active', label: 'Solo activos' },
    { value: 'inactive', label: 'Solo inactivos' },
  ];

  /** Filtro de texto en cliente: el listado llega completo y sin paginacion. */
  protected readonly visibleFieldTypes = computed(() => {
    const term = this.search().trim().toLowerCase();
    if (!term) return this.fieldTypes();

    return this.fieldTypes().filter(
      (item) =>
        item.fieldType.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term),
    );
  });

  protected readonly activeCount = computed(
    () => this.fieldTypes().filter((item) => item.status).length,
  );

  ngOnInit(): void {
    void this.load();
  }

  protected async load(): Promise<void> {
    const filter = this.statusFilter();
    const status = filter === 'all' ? null : filter === 'active';
    await this.fieldTypeService.getFieldTypes(status);
  }

  /**
   * `null` llega cuando se limpia el ng-select con el boton de "x"; se
   * resuelve exactamente igual que elegir "Todos los estados".
   */
  protected onStatusFilterChange(value: StatusFilter | null): void {
    this.statusFilter.set(value ?? 'all');
    void this.load();
  }

  protected onSearchChange(value: string): void {
    this.search.set(value);
  }

  protected async create(): Promise<void> {
    const result = await this.openModal(null);
    if (!result) return;

    const created = await this.fieldTypeService.createFieldType({
      fieldType: result.fieldType,
      description: result.description,
      protected: result.protected,
      multiline: result.multiline,
    });

    if (created) await this.load();
  }

  protected async edit(fieldType: FieldTypeModel): Promise<void> {
    const result = await this.openModal(fieldType);
    if (!result) return;

    const updated = await this.fieldTypeService.updateFieldType(fieldType.fieldType, {
      description: result.description,
      protected: result.protected,
      multiline: result.multiline,
      status: result.status,
    });

    if (updated) await this.load();
  }

  /** Reactivar es un PUT con `status = true`; el DELETE solo desactiva. */
  protected async activate(fieldType: FieldTypeModel): Promise<void> {
    const updated = await this.fieldTypeService.updateFieldType(fieldType.fieldType, {
      description: fieldType.description,
      protected: fieldType.protected,
      multiline: fieldType.multiline,
      status: true,
    });

    if (updated) await this.load();
  }

  /** Borrado SOFT: la confirmacion lo dice para no prometer una eliminacion. */
  protected async deactivate(fieldType: FieldTypeModel): Promise<void> {
    const confirmed = await this.confirmService.askDeactivate(
      'el tipo de campo',
      `${fieldType.fieldType} · ${fieldType.description}`,
    );

    if (!confirmed) return;

    const deleted = await this.fieldTypeService.deleteFieldType(fieldType.fieldType);
    if (deleted) await this.load();
  }

  private async openModal(fieldType: FieldTypeModel | null): Promise<IFieldTypeModalResult | null> {
    const reference = this.modalService.open(FieldTypeModalComponent, {
      centered: true,
      backdrop: 'static',
      ariaLabelledBy: 'ks-field-type-modal-title',
    });

    (reference.componentInstance as FieldTypeModalComponent).init(fieldType);

    try {
      return (await reference.result) as IFieldTypeModalResult;
    } catch {
      return null;
    }
  }
}
